import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PencilIcon, PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';

const FIELD_OPTIONS = [
  {
    value: 'numero_instalacao_enel',
    label: 'Numero Instalacao ENEL',
    helper: 'Capture o numero de instalacao no primeiro grupo: (...)',
  },
  {
    value: 'competencia',
    label: 'Competencia (MM/AAAA)',
    helper: 'Use dois grupos: mes e ano, nessa ordem.',
  },
  {
    value: 'periodo_faturamento',
    label: 'Periodo Faturamento',
    helper: 'Use dois grupos: data inicio e data fim (DD/MM/AAAA).',
  },
  {
    value: 'kwh_consumido',
    label: 'kWh Consumido',
    helper: 'Capture o numero no primeiro grupo.',
  },
  {
    value: 'kwh_compensado',
    label: 'kWh Compensado',
    helper: 'Capture o numero no primeiro grupo.',
  },
  {
    value: 'saldo_creditos_final',
    label: 'Saldo Creditos Final',
    helper: 'Capture o numero no primeiro grupo.',
  },
  {
    value: 'valor_total_enel',
    label: 'Valor Total ENEL',
    helper: 'Capture o numero no primeiro grupo.',
  },
];

const EMPTY_FORM = {
  id: null,
  field_key: FIELD_OPTIONS[0].value,
  regex_pattern: '',
  regex_flags: 'i',
  prioridade: 100,
  descricao: '',
  ativo: true,
};

const FIELD_LABEL_BY_KEY = FIELD_OPTIONS.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

const SolarEnelImportMappingsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [testText, setTestText] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const selectedField = useMemo(
    () => FIELD_OPTIONS.find((item) => item.value === form.field_key) || FIELD_OPTIONS[0],
    [form.field_key]
  );

  const loadMappings = async (nextIncludeInactive = includeInactive) => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/solar/enel-import-mappings', {
        params: {
          include_inactive: nextIncludeInactive ? '1' : undefined,
        },
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar mapeamentos ENEL');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMappings();
  }, []);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setTestText('');
    setTestResult(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item.id,
      field_key: item.field_key,
      regex_pattern: item.regex_pattern || '',
      regex_flags: item.regex_flags || 'i',
      prioridade: Number(item.prioridade || 100),
      descricao: item.descricao || '',
      ativo: item.ativo !== false,
    });
    setTestText('');
    setTestResult(null);
    setShowModal(true);
  };

  const runRegexTest = async () => {
    if (!String(form.regex_pattern || '').trim()) {
      toast.error('Informe a regex para testar');
      return;
    }

    if (!String(testText || '').trim()) {
      toast.error('Informe um texto de teste');
      return;
    }

    setTestLoading(true);
    try {
      const { data } = await axios.post('/api/solar/enel-import-mappings/test-regex', {
        field_key: form.field_key,
        regex_pattern: form.regex_pattern,
        regex_flags: form.regex_flags || 'i',
        test_text: testText,
      });
      setTestResult(data?.result || null);
      if (data?.result?.matched) {
        toast.success('Regex encontrou correspondencia');
      } else {
        toast.error('Regex nao encontrou correspondencia');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao testar regex');
    } finally {
      setTestLoading(false);
    }
  };

  const save = async () => {
    const payload = {
      field_key: form.field_key,
      regex_pattern: String(form.regex_pattern || '').trim(),
      regex_flags: String(form.regex_flags || 'i').trim() || 'i',
      prioridade: Number(form.prioridade || 100),
      descricao: String(form.descricao || '').trim() || null,
      ativo: form.ativo,
    };

    if (!payload.regex_pattern) {
      toast.error('Regex obrigatoria');
      return;
    }

    try {
      // Validacao basica no frontend para feedback imediato.
      // eslint-disable-next-line no-new
      new RegExp(payload.regex_pattern, payload.regex_flags);
    } catch (_) {
      toast.error('Regex invalida para as flags informadas');
      return;
    }

    try {
      if (form.id) {
        await axios.put(`/api/solar/enel-import-mappings/${form.id}`, payload);
        toast.success('Mapeamento atualizado');
      } else {
        await axios.post('/api/solar/enel-import-mappings', payload);
        toast.success('Mapeamento criado');
      }

      setShowModal(false);
      setForm(EMPTY_FORM);
      await loadMappings();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar mapeamento');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Deseja desativar esta regra?')) return;
    try {
      await axios.delete(`/api/solar/enel-import-mappings/${id}`);
      toast.success('Regra desativada');
      await loadMappings();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao remover mapeamento');
    }
  };

  const toggleIncludeInactive = async (checked) => {
    setIncludeInactive(checked);
    await loadMappings(checked);
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('pt-BR');
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Solar · Mapeamentos ENEL</h1>
          <p className="text-gray-600">Regras de extracao para importacao de PDF das faturas ENEL</p>
        </div>
        <div className="flex items-center gap-3">
          {loading && <div className="loader h-6 w-6" />}
          <button className="btn-primary" onClick={openNew}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Nova Regra
          </button>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body flex items-center justify-between gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={includeInactive} onChange={(e) => toggleIncludeInactive(e.target.checked)} />
            Exibir regras inativas
          </label>
          <button className="btn-secondary" onClick={() => loadMappings()}>
            Atualizar
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Regras ({items.length})</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Campo</th>
                  <th>Regex</th>
                  <th>Flags</th>
                  <th>Prioridade</th>
                  <th>Status</th>
                  <th>Atualizada em</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{FIELD_LABEL_BY_KEY[item.field_key] || item.field_key}</td>
                    <td className="font-mono text-xs">{item.regex_pattern}</td>
                    <td className="uppercase">{item.regex_flags || 'i'}</td>
                    <td>{item.prioridade}</td>
                    <td>
                      <span className={item.ativo ? 'badge-success' : 'badge-danger'}>{item.ativo ? 'Ativa' : 'Inativa'}</span>
                    </td>
                    <td>{formatDateTime(item.updated_at || item.created_at)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button className="text-primary-600 hover:text-primary-900 p-1" onClick={() => openEdit(item)}>
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button className="text-danger-600 hover:text-danger-900 p-1" onClick={() => remove(item.id)}>
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-gray-500 py-8">
                      Nenhuma regra cadastrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">{form.id ? 'Editar Regra' : 'Nova Regra'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="form-label">Campo alvo</label>
                <select className="form-select" value={form.field_key} onChange={(e) => setForm({ ...form, field_key: e.target.value })}>
                  {FIELD_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">{selectedField.helper}</p>
              </div>

              <div className="md:col-span-2">
                <label className="form-label">Regex</label>
                <textarea
                  rows={4}
                  className="form-input font-mono text-xs"
                  value={form.regex_pattern}
                  onChange={(e) => setForm({ ...form, regex_pattern: e.target.value })}
                  placeholder="Ex.: kwh\\s*consumido[\\s\\S]{0,60}?([0-9.,]+)"
                />
              </div>

              <div>
                <label className="form-label">Flags</label>
                <input
                  className="form-input"
                  value={form.regex_flags}
                  onChange={(e) => setForm({ ...form, regex_flags: e.target.value })}
                  placeholder="i"
                />
              </div>

              <div>
                <label className="form-label">Prioridade</label>
                <input
                  className="form-input"
                  type="number"
                  value={form.prioridade}
                  onChange={(e) => setForm({ ...form, prioridade: e.target.value })}
                />
              </div>

              <div className="md:col-span-2">
                <label className="form-label">Descricao (opcional)</label>
                <input
                  className="form-input"
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                />
              </div>

              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} />
                  Regra ativa
                </label>
              </div>

              <div className="md:col-span-2 border-t pt-4 mt-2">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <label className="form-label mb-0">Teste da Regex</label>
                  <button className="btn-secondary" type="button" onClick={runRegexTest} disabled={testLoading}>
                    {testLoading ? 'Testando...' : 'Testar Regex'}
                  </button>
                </div>
                <textarea
                  rows={5}
                  className="form-input font-mono text-xs"
                  value={testText}
                  onChange={(e) => setTestText(e.target.value)}
                  placeholder="Cole aqui um trecho textual da fatura ENEL para validar a regra."
                />

                {testResult && (
                  <div className="mt-3 p-3 rounded-md border border-gray-200 bg-gray-50 space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Status:</span>
                      <span className={testResult.matched ? 'badge-success' : 'badge-danger'}>
                        {testResult.matched ? 'MATCH' : 'SEM MATCH'}
                      </span>
                    </div>

                    <div>
                      <p className="text-gray-600">Valor interpretado</p>
                      <pre className="text-xs font-mono whitespace-pre-wrap break-words bg-white border border-gray-200 rounded p-2">
                        {testResult.parsed_value != null ? JSON.stringify(testResult.parsed_value, null, 2) : 'null'}
                      </pre>
                    </div>

                    <div>
                      <p className="text-gray-600">Trecho encontrado</p>
                      <pre className="text-xs font-mono whitespace-pre-wrap break-words bg-white border border-gray-200 rounded p-2">
                        {testResult.matched_text || '-'}
                      </pre>
                    </div>

                    <div>
                      <p className="text-gray-600">Grupos capturados</p>
                      <pre className="text-xs font-mono whitespace-pre-wrap break-words bg-white border border-gray-200 rounded p-2">
                        {Array.isArray(testResult.capture_groups) && testResult.capture_groups.length > 0
                          ? JSON.stringify(testResult.capture_groups, null, 2)
                          : '[]'}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={save}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SolarEnelImportMappingsPage;
